import React, { useMemo } from 'react';
import Icon from '../../components/Icon';
import TransportChip from './TransportChip';
import { useIdentity } from '../../hooks/useIdentity';
import './FileCard.css';

// Mapping from MIME to icon kind. We render the 'image' icon for any
// `image/*` mime so the visual matches what the recipient is about to
// download; everything else falls through to the generic file glyph.
function deriveIconKind(mime) {
  if (typeof mime === 'string' && mime.toLowerCase().startsWith('image/')) {
    return 'image';
  }
  return 'file';
}

// Pretty-print a byte count. Returns "1.2 MB", "340 KB", "512 B".
// Localised to the user's locale via Intl.NumberFormat so the decimal
// separator follows the OS — design used "1.2 MB" in en-US.
const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;
function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < KB) return `${n} B`;
  if (n < MB) return `${(n / KB).toFixed(0)} KB`;
  if (n < GB) return `${(n / MB).toFixed(1)} MB`;
  return `${(n / GB).toFixed(2)} GB`;
}

/**
 * F11.2 — file-transfer progress card.
 *
 * Pure view. The parent feeds us a live `Transfer` (from the transfer
 * store) and wires the pause / resume / cancel callbacks back to the
 * envelope dispatcher. No data fetching, no envelope traffic from here.
 *
 * Direction (outgoing vs incoming) is derived from `transfer.fromId`
 * compared against `useIdentity().user`. Outgoing cards use the cocoa-bg
 * + light ink palette (matches `.bubble.self`); incoming cards use the
 * frost-bg + dark ink palette (matches `.bubble.other`).
 *
 * Props:
 *   - `transfer`: the live record from the store.
 *   - `onPause` / `onResume` / `onCancel`: optional callbacks. When
 *     omitted the corresponding affordance is rendered disabled (so a
 *     read-only card — e.g. a late-joiner reading history — still
 *     reflects state without exposing controls that don't work).
 */
const FileCard = ({ transfer, onPause, onResume, onCancel }) => {
  const { user } = useIdentity();

  // Read-mode for a transfer that was sent before this client joined the
  // mesh — render a static "done" card. The Bubble layer handles building
  // the synthetic transfer object; here we just present what we're given.
  if (!transfer) return null;

  const {
    name,
    size,
    mime,
    fromId,
    progress = 0,
    state = 'pending',
    via,
    speedMBps = 0,
    downloadUrl,
  } = transfer;

  // Transport vocabulary is `lan | cloud`. LAN/WebRTC file transfers are the
  // Beam default (F11.4 Firebase cloud fallback was cancelled 2026-05-29).
  // S8 adds a Supabase-only "share over the internet" path: those transfers
  // carry `via: 'cloud'` and a `downloadUrl` pointing at Supabase Storage.
  // A cloud transfer is recognised by `via === 'cloud'` AND a usable URL —
  // legacy rows that carry a stray `via: 'cloud'` without a URL still fall
  // back to the LAN chip, since their bytes only ever flowed over WebRTC.
  const isCloud = (via === 'cloud') && typeof downloadUrl === 'string' && downloadUrl.length > 0;
  const chipVia = isCloud ? 'cloud' : 'lan';

  // Direction. `fromId === user` ⇒ this card is for an outgoing transfer
  // we initiated. Otherwise the bytes are inbound.
  const isOutgoing = fromId === user;
  const dir = isOutgoing ? 'out' : 'in';

  const done = state === 'done' || progress >= 100;
  const failed = state === 'failed';
  const paused = state === 'paused';
  const active = state === 'active' && !done && !failed;

  // Direction-driven palette. We inline these like the design source so
  // a stylesheet refactor doesn't have to chase two surfaces.
  const cardStyle = {
    background: dir === 'out' ? 'rgba(91,42,42,.5)' : 'rgba(255,250,248,.9)',
    border:
      dir === 'out'
        ? '1px solid rgba(255,200,180,.18)'
        : '1px solid rgba(255,255,255,.6)',
    color: dir === 'out' ? '#fff6ee' : '#181f33',
  };

  const iconKind = deriveIconKind(mime);
  const iconBg =
    iconKind === 'image' ? 'rgba(155,107,255,.18)' : 'rgba(63,220,138,.16)';
  const iconStroke = iconKind === 'image' ? '#c9a8ff' : 'var(--lan)';

  const subColor = dir === 'out' ? 'rgba(255,255,255,.7)' : '#6b7892';
  const doneColor = dir === 'out' ? '#9affc9' : '#1c9a52';

  const progressBg = isCloud ? 'var(--cloud)' : 'var(--lan)';
  const progressTrackStyle = dir === 'out' ? { background: 'rgba(0,0,0,.28)' } : undefined;

  // Action icon — pause while active; check after a clean outgoing
  // transfer; download once the incoming blob is ready; alert on failure.
  const actionIcon = failed
    ? 'alert'
    : done
      ? (isOutgoing ? 'check' : 'download')
      : 'pause';

  // Speed text. Reduced-motion users still see the number; we just keep
  // the layout stable. Active = "37% · 11.2 MB/s"; finished = "sent" /
  // "received"; failed = "failed".
  const speedText = useMemo(() => {
    if (failed) return 'Transfer failed';
    if (done) return isOutgoing ? 'sent' : 'received';
    if (paused) return `${Math.round(progress)}% · paused`;
    return `${Math.round(progress)}% · ${(Number(speedMBps) || 0).toFixed(1)} MB/s`;
  }, [failed, done, isOutgoing, paused, progress, speedMBps]);

  const actionLabel = failed
    ? 'Transfer failed'
    : done
      ? (isOutgoing ? 'Sent' : 'Download file')
      : paused
        ? 'Resume transfer'
        : 'Pause transfer';

  // Wire the primary action button. Pause/resume during active flow;
  // disabled when there's no useful action (sender's "sent" state).
  const handlePrimary = () => {
    if (failed) return;
    if (paused && typeof onResume === 'function') return onResume();
    if (active && typeof onPause === 'function') return onPause();
  };

  const primaryDisabled =
    failed ||
    done ||
    (active && typeof onPause !== 'function') ||
    (paused && typeof onResume !== 'function');

  // Cancel only renders when (a) we have a handler and (b) the transfer
  // is still in flight. The design shows cancel via a secondary "✕"
  // affordance; we render it next to the primary action so screen-reader
  // users get an explicit button rather than a long-press gesture.
  const canCancel =
    typeof onCancel === 'function' && !done && !failed;

  return (
    <div
      className={`file-card${failed ? ' failed' : ''}`}
      data-testid="file-card"
      data-dir={dir}
      data-state={state}
      style={cardStyle}
    >
      <div
        className="fc-ico"
        style={{ background: iconBg, border: `1px solid ${iconStroke}55` }}
      >
        <Icon
          name={failed ? 'alert' : iconKind}
          size={20}
          stroke={failed ? '#ff8a8a' : iconStroke}
        />
      </div>
      <div className="fc-meta">
        <b title={name}>{name}</b>
        <span className="sub" style={{ color: subColor }}>
          <span data-testid="file-card-size">{formatBytes(size)}</span>
          <TransportChip via={chipVia} size="sm" />
          {done ? (
            <span className="done-mark" style={{ color: doneColor }}>
              <Icon
                name={isOutgoing ? 'check' : 'download'}
                size={11}
                stroke="currentColor"
              />
              <span data-testid="file-card-status">{speedText}</span>
            </span>
          ) : (
            <span data-testid="file-card-status">{speedText}</span>
          )}
        </span>
        {!done && !failed && (
          <div className="progress" style={progressTrackStyle}>
            <i
              data-testid="file-card-progress-fill"
              style={{ width: `${progress}%`, background: progressBg }}
            />
          </div>
        )}
      </div>
      {isCloud && done ? (
        // S8 — cloud file. The bytes live in Supabase Storage; the recipient
        // (and the sender, who may want their own copy) downloads via the
        // public URL rather than a P2P blob. Rendered as an anchor so it's a
        // real link: keyboard-operable, right-click "Save as", screen-reader
        // announced as a link. `download` hints the original filename.
        <a
          className="fc-action"
          href={downloadUrl}
          download={name || undefined}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Download ${name || 'file'}`}
          data-testid="file-card-download"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: dir === 'out' ? 'rgba(255,255,255,.1)' : '#eef1f7',
            color: dir === 'out' ? '#fff' : '#4a5570',
          }}
        >
          <Icon name="download" size={16} stroke="currentColor" />
        </a>
      ) : (
        <button
          type="button"
          className="fc-action"
          aria-label={actionLabel}
          onClick={handlePrimary}
          disabled={primaryDisabled}
          style={{
            background: dir === 'out' ? 'rgba(255,255,255,.1)' : '#eef1f7',
            color: dir === 'out' ? '#fff' : '#4a5570',
          }}
        >
          <Icon name={actionIcon} size={16} stroke="currentColor" />
        </button>
      )}
      {canCancel && (
        <button
          type="button"
          className="fc-action"
          aria-label="Cancel transfer"
          onClick={onCancel}
          style={{
            background: dir === 'out' ? 'rgba(255,255,255,.1)' : '#eef1f7',
            color: dir === 'out' ? '#fff' : '#4a5570',
          }}
        >
          <Icon name="close" size={16} stroke="currentColor" />
        </button>
      )}
    </div>
  );
};

export default FileCard;
export { formatBytes as __formatBytes, deriveIconKind as __deriveIconKind };
